import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './Chatbot.css';

const Chatbot = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState('welcome');
  const [userData, setUserData] = useState({
    fullName: '',
    phone: '',
    projectType: '',
    projectDetails: ''
  });
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const messagesEndRef = useRef(null);

  const projectTypes = [
    { id: 'villa', emoji: '🏡' },
    { id: 'residential', emoji: '🏘️' },
    { id: 'corporate', emoji: '🏢' },
    { id: 'infrastructure', emoji: '🏗️' }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Auto-open chatbot after 15 seconds
  useEffect(() => {
    if (!hasAutoOpened) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        setHasAutoOpened(true);
      }, 15000); // 15 seconds

      return () => clearTimeout(timer);
    }
  }, [hasAutoOpened]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTimeout(() => {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          addBotMessage(t('chatbot.welcome'));
          
          setTimeout(() => {
            setIsTyping(true);
            setTimeout(() => {
              setIsTyping(false);
              addBotMessage(t('chatbot.askProjectType'));
              setCurrentStep('projectType');
            }, 800);
          }, 1500);
        }, 1000);
      }, 500);
    }
  }, [isOpen, t]);

  const addBotMessage = (text) => {
    setMessages(prev => [...prev, { type: 'bot', text, timestamp: new Date() }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: 'user', text, timestamp: new Date() }]);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const value = inputValue.trim();
    addUserMessage(value);
    setInputValue('');

    setTimeout(() => {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        processStep(value);
      }, 1200);
    }, 300);
  };

  const handleProjectTypeSelect = (projectType) => {
    const selectedProject = projectTypes.find(p => p.id === projectType);
    const projectLabel = t(`chatbot.projectTypes.${projectType}`);
    addUserMessage(`${selectedProject.emoji} ${projectLabel}`);
    setUserData(prev => ({ ...prev, projectType: projectLabel }));
    
    setTimeout(() => {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        const message = t('chatbot.projectSelected', { 
          emoji: selectedProject.emoji, 
          projectType: projectLabel 
        });
        addBotMessage(message);
        
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            addBotMessage(t('chatbot.askFullName'));
            setCurrentStep('fullName');
          }, 800);
        }, 1500);
      }, 1000);
    }, 300);
  };

  const processStep = (value) => {
    switch (currentStep) {
      case 'fullName':
        setUserData(prev => ({ ...prev, fullName: value }));
        addBotMessage(t('chatbot.niceToMeet', { name: value }));
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            addBotMessage(t('chatbot.askPhone'));
            setCurrentStep('phone');
          }, 800);
        }, 1000);
        break;

      case 'phone':
        setUserData(prev => ({ ...prev, phone: value }));
        addBotMessage(t('chatbot.thanks'));
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            addBotMessage(t('chatbot.askProjectDetails'));
            setCurrentStep('projectDetails');
          }, 800);
        }, 1000);
        break;

      case 'projectDetails':
        const skipWords = ['geç', 'atla', 'skip', 'pass', 'пропустить', 'überspringen', 'تخطي'];
        const isSkipped = skipWords.some(word => value.toLowerCase().includes(word));
        const details = isSkipped ? t('chatbot.notSpecified') || 'Not specified' : value;
        setUserData(prev => ({ ...prev, projectDetails: details }));
        addBotMessage(t('chatbot.allInfoReceived'));
        setTimeout(() => {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            addBotMessage(t('chatbot.messageSent'));
            setTimeout(() => {
              addBotMessage(t('chatbot.goodbye'));
            }, 2000);
          }, 1000);
        }, 1000);
        setCurrentStep('completed');
        break;

      default:
        addBotMessage(t('chatbot.welcome'));
    }
  };

  // Bilgileri kaydet (ileride backend'e göndermek için hazır)
  const saveContactInfo = (fullName, phone, projectType, projectDetails) => {
    const contactData = {
      fullName,
      phone,
      projectType,
      projectDetails,
      timestamp: new Date().toISOString(),
      source: 'www.gedikgroup.tr - Chatbot'
    };
    
    // Console'a yazdır (geliştirme için)
    console.log('📬 Yeni İletişim Talebi:', contactData);
    
    // LocalStorage'a kaydet (ileride backend eklenmesi için)
    try {
      const existingContacts = JSON.parse(localStorage.getItem('gedik_contacts') || '[]');
      existingContacts.push(contactData);
      localStorage.setItem('gedik_contacts', JSON.stringify(existingContacts));
      console.log('✅ Bilgiler kaydedildi');
    } catch (error) {
      console.error('❌ Kayıt hatası:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button 
        className={`chat-button-elegant ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
      >
        <div className="chat-button-glow"></div>
        <MessageCircle size={28} strokeWidth={2} />
        <div className="chat-notification-badge">1</div>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window-elegant">
          <div className="chat-gradient-bg"></div>
          
          {/* Header */}
          <div className="chat-header-elegant">
            <div className="chat-header-content-elegant">
              <div className="chat-avatar">
                <img 
                  src="/gedik-logo.png" 
                  alt="Gedik Group"
                  className="chat-avatar-logo"
                />
                <div className="avatar-status"></div>
              </div>
              <div className="chat-info">
                <h3 className="chat-title-elegant">{t('chatbot.title')}</h3>
                <p className="chat-status-elegant">
                  <span className="status-dot"></span>
                  {t('chatbot.online')}
                </p>
              </div>
            </div>
            <button className="chat-close-elegant" onClick={() => setIsOpen(false)}>
              <X size={20} strokeWidth={2} />
            </button>
          </div>

          {/* Messages */}
          <div className="chat-messages-elegant">
            {messages.map((msg, index) => (
              <div key={index} className={`message-elegant ${msg.type}`}>
                {msg.type === 'bot' && (
                  <div className="message-avatar-small">
                    <Sparkles size={14} />
                  </div>
                )}
                <div className="message-content-wrapper">
                  <div className="message-bubble-elegant">
                    {msg.text}
                  </div>
                  <span className="message-time">{formatTime(msg.timestamp)}</span>
                </div>
              </div>
            ))}
            
            {/* Project Type Selection Buttons */}
            {currentStep === 'projectType' && !isTyping && (
              <div className="project-type-options">
                {projectTypes.map((project) => (
                  <button
                    key={project.id}
                    className="project-type-button"
                    onClick={() => handleProjectTypeSelect(project.id)}
                  >
                    <span className="project-emoji">{project.emoji}</span>
                    <span className="project-label">{t(`chatbot.projectTypes.${project.id}`)}</span>
                  </button>
                ))}
              </div>
            )}
            
            {isTyping && (
              <div className="message-elegant bot">
                <div className="message-avatar-small">
                  <Sparkles size={14} />
                </div>
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {currentStep !== 'completed' && (
            <div className="chat-input-container-elegant">
              <input
                type="text"
                className="chat-input-elegant"
                placeholder={t('chatbot.placeholder')}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isTyping}
              />
              <button 
                className="chat-send-elegant" 
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
              >
                <Send size={18} strokeWidth={2.5} />
              </button>
            </div>
          )}
          
          <div className="chat-footer-brand">
            <p className="chatbot-footer-text">{t('chatbot.footer')}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
